import React, { Component } from 'react';
import { connect } from 'react-redux';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import { DROPDOWN_CONFIG, DATATABLE_CONFIG, USER_SUBMENU_ID } from '../../../shared/Config';
import { HASACCESS } from '../../../shared/utility';
import Messages from '../../../shared/UI/Messages/Messages';
import * as actions from '../../../store/actions/index';

// ── Dummy data ────────────────────────────────────────────────────────────────
// Columns: A=openingStock, B=purchaseSalesReturn, C=excessRawMaterial,
//          D=stockConsumed, E=wastage, F=normalLoss,
//          G=salesInternalTransferPurchaseReturn, H=shortageRawMaterial,
//          I=effectDueToConversion
// Computed: totalStock = A+B+C, totalConsumed = D+E+F+G+H,
//           netStockCalc = A+B-D-E-F-G+I, difference = netStockCalc - netStockSystem
const DUMMY_DATA = [
    {
        id: 1,
        category: 'Hygiene',
        itemName: 'Test_raw_material [Piece]',
        A: -21.000, B: 0.000,  C: 43.000,
        D: 0.000,   E: 0.000,  F: 0.000,
        G: 0.000,   H: 1.000,  I: 0.000,
        netStockSystem: 0.000,
    },
    {
        id: 2,
        category: 'Hygiene',
        itemName: 'Test_fruit [Kg]',
        A: 61.000, B: 0.000, C: 0.000,
        D: 0.000,  E: 0.000, F: 0.000,
        G: 0.000,  H: 0.000, I: 0.000,
        netStockSystem: 0.000,
    },
    {
        id: 3,
        category: 'Hygiene',
        itemName: 'Test_hygeine_conversion [Piece]',
        A: 2.000,  B: 0.000, C: 0.000,
        D: 0.000,  E: 0.000, F: 0.000,
        G: 0.000,  H: 0.000, I: 0.000,
        netStockSystem: 0.000,
    },
    {
        id: 4,
        category: 'Vegetables',
        itemName: 'Tomato [Kg]',
        A: 50.000, B: 10.000, C: 0.000,
        D: 20.000, E: 2.000,  F: 1.000,
        G: 0.000,  H: 0.000,  I: 0.000,
        netStockSystem: 37.000,
    },
    {
        id: 5,
        category: 'Vegetables',
        itemName: 'Onion [Kg]',
        A: 30.000, B: 5.000, C: 0.000,
        D: 10.000, E: 1.000, F: 0.000,
        G: 0.000,  H: 0.000, I: 0.000,
        netStockSystem: 24.000,
    },
    {
        id: 6,
        category: 'Grains',
        itemName: 'Rice [Kg]',
        A: 100.000, B: 20.000, C: 0.000,
        D: 40.000,  E: 3.000,  F: 2.000,
        G: 0.000,   H: 0.000,  I: 0.000,
        netStockSystem: 75.000,
    },
];

// ── Computed fields ───────────────────────────────────────────────────────────
const withComputedFields = (rows) =>
    rows.map(row => {
        const totalStock    = row.A + row.B + row.C;
        const totalConsumed = row.D + row.E + row.F + row.G + row.H;
        const netStockCalc  = row.A + row.B - row.D - row.E - row.F - row.G + row.I;
        const difference    = netStockCalc - row.netStockSystem;
        return { ...row, totalStock, totalConsumed, netStockCalc, difference };
    });

const fmt = (val) =>
    typeof val === 'number' ? val.toFixed(3) : val;

class StockSummaryReport extends Component {
    state = {
        rawMaterial:  '',
        category:     null,
        unitType:     null,
        fromDate:     new Date('2023-09-26'),
        toDate:       new Date('2023-09-26'),
        tableData:    [],
        searched:     false,
        globalFilter: null,
    };

    componentDidMount() {
        this.getAccess = HASACCESS(USER_SUBMENU_ID);
    }

    handleSearch = () => {
        const { fromDate, toDate } = this.state;
        if (!fromDate || !toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'Please select From Date and To Date' });
            return;
        }
        if (fromDate > toDate) {
            this.growl.show({ severity: 'error', summary: 'Error', detail: 'From Date cannot be after To Date' });
            return;
        }
        // Filter by rawMaterial text if entered
        const filtered = this.state.rawMaterial
            ? DUMMY_DATA.filter(r =>
                r.itemName.toLowerCase().includes(this.state.rawMaterial.toLowerCase()))
            : DUMMY_DATA;
        this.setState({ tableData: withComputedFields(filtered), searched: true });
    };

    handleExport = () => {
        this.growl.show({ severity: 'info', summary: 'Export', detail: 'Export triggered' });
    };

    // ── Number cell template ──────────────────────────────────────────────────
    numTemplate = (field) => (rowData) => (
        <span>{fmt(rowData[field])}</span>
    );

    render() {
        if (this.getAccess !== undefined && this.getAccess.viewAccess === false) {
            this.props.history.push('/dashboard');
        }

        const { tableData, searched, fromDate, toDate } = this.state;

        const header = (
            <div style={{ textAlign: 'right' }}>
                <InputText
                    type="search"
                    onInput={(e) => this.setState({ globalFilter: e.target.value })}
                    placeholder="Search..."
                />
            </div>
        );

        // Multi-line column header helper
        const multiHeader = (lines) => (
            <div style={{ textAlign: 'center', lineHeight: '1.3' }}>
                {lines.map((line, i) => <div key={i}>{line}</div>)}
            </div>
        );

        return (
            <div className="p-grid padding">
                {this.props.loading  && <div className="page-loader"></div>}
                {this.props.error   !== null && <Messages type="error"   message={this.props.error} />}
                {this.props.success !== null && <Messages type="success" message={this.props.success} />}
                <Toast ref={(el) => this.growl = el} />

                <div className="p-col-12">
                    <div className="card card-w-title" style={{ position: 'relative' }}>
                        <h1>Stock Summary Report</h1>

                        {/* Export — top right */}
                        <div className="p-d-flex p-jc-end p-mb-3">
                            <Button
                                label="Export"
                                icon="pi pi-file-excel"
                                className="p-button-warning"
                                onClick={this.handleExport}
                            />
                        </div>

                        {/* ── Filter Row ────────────────────────────────────── */}
                        <div className="p-grid p-align-end">

                            {/* Raw Material */}
                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Raw Material</label>
                                <InputText
                                    placeholder="Raw Material"
                                    className="p-col-12"
                                    value={this.state.rawMaterial}
                                    onChange={(e) => this.setState({ rawMaterial: e.target.value })}
                                />
                            </div>

                            {/* Category */}
                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Category</label>
                                <Dropdown
                                    placeholder="Category"
                                    options={[
                                        { label: 'Select Category', value: null },
                                        { label: 'Hygiene',         value: 'hygiene'    },
                                        { label: 'Vegetables',      value: 'vegetables' },
                                        { label: 'Grains',          value: 'grains'     },
                                    ]}
                                    value={this.state.category || null}
                                    onChange={(e) => this.setState({ category: e.value })}
                                    // filter
                                    // autoWidth
                                    // className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            {/* Unit Type */}
                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">Unit Type</label>
                                <Dropdown
                                    placeholder="Purchase Unit"
                                    options={[
                                        { label: 'Purchase Unit', value: 'purchase' },
                                        { label: 'Sales Unit',    value: 'sales'    },
                                        { label: 'Base Unit',     value: 'base'     },
                                    ]}
                                    value={this.state.unitType || null}
                                    onChange={(e) => this.setState({ unitType: e.value })}
                                    // autoWidth
                                    // className="p-col-12"
                                    {...DROPDOWN_CONFIG}
                                />
                            </div>

                            {/* From Date */}
                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">From Date</label>
                                <Calendar
                                    placeholder="From Date"
                                    value={fromDate}
                                    onChange={(e) => this.setState({ fromDate: e.value })}
                                    // dateFormat="d M yy"
                                    // showIcon
                                    // className="p-col-12"
                                />
                            </div>

                            {/* To Date */}
                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-flex-column p-gap-2">
                                <label className="label">To Date</label>
                                <Calendar
                                    placeholder="To Date"
                                    value={toDate}
                                    onChange={(e) => this.setState({ toDate: e.value })}
                                    // dateFormat="d M yy"
                                    // showIcon
                                    // className="p-col-12"
                                />
                            </div>

                            {/* Search */}
                            <div className="p-col-12 p-md-2 p-lg-2 p-d-flex p-ai-end">
                                <Button
                                    label="Search"
                                    className="p-button-primary"
                                    onClick={this.handleSearch}
                                />
                            </div>

                        </div>
                        {/* ── End Filter Row ─────────────────────────────────── */}

                        {/* ── DataTable ─────────────────────────────────────── */}
                        {searched && (
                            <div className="p-col-12" style={{ marginTop: '1.5em' }}>
                                <DataTable
                                    value={tableData}
                                    globalFilter={this.state.globalFilter}
                                    header={header}
                                    scrollable
                                    scrollDirection="both"
                                    scrollHeight="520px"
                                    style={{ width: '100%' }}
                                    {...DATATABLE_CONFIG}
                                >
                                    <Column field="category" header="Category"  sortable style={{ minWidth: '130px' }} />
                                    <Column field="itemName" header="Item Name" sortable style={{ minWidth: '220px' }} />

                                    <Column
                                        field="A"
                                        header={multiHeader(['Opening', 'Stock', '(A)'])}
                                        body={this.numTemplate('A')}
                                        sortable
                                        style={{ minWidth: '110px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="B"
                                        header={multiHeader(['Purchase /', 'Sales Return', '(B)'])}
                                        body={this.numTemplate('B')}
                                        sortable
                                        style={{ minWidth: '120px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="C"
                                        header={multiHeader(['Excess of', 'Raw Material', '(C)'])}
                                        body={this.numTemplate('C')}
                                        sortable
                                        style={{ minWidth: '120px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="totalStock"
                                        header={multiHeader(['Total Stock', '(A+B+C)'])}
                                        body={this.numTemplate('totalStock')}
                                        sortable
                                        style={{ minWidth: '120px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="D"
                                        header={multiHeader(['Stock', 'Consumed', '(D)'])}
                                        body={this.numTemplate('D')}
                                        sortable
                                        style={{ minWidth: '110px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="E"
                                        header={multiHeader(['Wastage', '(E)'])}
                                        body={this.numTemplate('E')}
                                        sortable
                                        style={{ minWidth: '100px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="F"
                                        header={multiHeader(['Normal', 'Loss', '(F)'])}
                                        body={this.numTemplate('F')}
                                        sortable
                                        style={{ minWidth: '100px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="G"
                                        header={multiHeader(['Sales / Internal', 'Transfer /', 'Purchase Return', '(G)'])}
                                        body={this.numTemplate('G')}
                                        sortable
                                        style={{ minWidth: '150px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="H"
                                        header={multiHeader(['Shortage of', 'Raw Material', '(H)'])}
                                        body={this.numTemplate('H')}
                                        sortable
                                        style={{ minWidth: '120px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="I"
                                        header={multiHeader(['Effect due', 'to Conversion', '(I)'])}
                                        body={this.numTemplate('I')}
                                        sortable
                                        style={{ minWidth: '130px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="totalConsumed"
                                        header={multiHeader(['Total Consumed', 'Stock', '(D+E+F+G+H)'])}
                                        body={this.numTemplate('totalConsumed')}
                                        sortable
                                        style={{ minWidth: '140px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="netStockSystem"
                                        header={multiHeader(['Net Stock', 'System'])}
                                        body={this.numTemplate('netStockSystem')}
                                        sortable
                                        style={{ minWidth: '110px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="netStockCalc"
                                        header={multiHeader(['Net Stock', 'Calculate', '(A+B-D-E-F-G+I)'])}
                                        body={this.numTemplate('netStockCalc')}
                                        sortable
                                        style={{ minWidth: '150px', textAlign: 'right' }}
                                    />
                                    <Column
                                        field="difference"
                                        header="Difference"
                                        body={this.numTemplate('difference')}
                                        sortable
                                        style={{ minWidth: '110px', textAlign: 'right' }}
                                    />
                                </DataTable>
                            </div>
                        )}

                        {/* Empty state */}
                        {!searched && (
                            <div
                                className="p-d-flex p-jc-center p-ai-center"
                                style={{ minHeight: '200px', color: '#aaa', fontSize: '15px' }}
                            >
                                <i className="pi pi-search p-mr-2" />
                                Select filters and click Search to view the report.
                            </div>
                        )}

                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    loggedUserDetail: state.auth.userObject,
    loading:          state.master.loading,
    error:            state.master.error,
    success:          state.master.success,
    tableLists:       state.master.tableLists,
});

const mapDispatchToProps = dispatch => ({
    dynamicDeleteProcess: (model, data, func) => dispatch(actions.dynamicDeleteProcess(model, data, func)),
    getMasterTableList:   (table)             => dispatch(actions.getMasterTableList(table)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StockSummaryReport);